import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import {
  AlertController,
  ModalController,
  PopoverController,
} from "ionic-angular";
import SortOptions from "models/content-wrapper/sort-options";
import * as moment from "moment-timezone";
import { SystemAdminService } from "providers/system-admin/system-admin.service";
import { UtilityService } from "providers/utility-service/utility-service";
import { take } from "rxjs/operators";
import { WorkforceActivity } from "../../../models/system-admin/workforce-activity";
import { WorkforceActivityDetails } from "../../../models/system-admin/workforce-activity-details";
import { ResourceManagementService } from "../../../providers/resource-management/resource-management.service";
import { ActivityLogAddComponent } from "../activity-log-add/activity-log-add";

@Component({
  selector: "activity-log-details",
  templateUrl: "activity-log-details.html",
})
export class ActivityLogDetailsComponent implements OnInit, OnDestroy {
  private pageSize: number = 10;
  public currentPage: number = 0;
  public totalPageCount: number;
  private selectedDisciplinesFilter: string[] = [];
  private selectedActivityType: string;
  private selectedStartDate: string = "";
  private selectedEndDate: string = "";
  private activityLog: WorkforceActivityDetails[] = [];
  private query: string = "";
  private activityLogValue: WorkforceActivity;
  private hasAddAccess: boolean;
  private hasEditAccess: boolean;
  private hasDeleteAccess: boolean;
  private filterParameters: any = {};
  private minDateValue: any = { year: 1900, month: 1, day: 1 };
  dateRange: any;
  public hireToPresent: boolean;
  sortOptions: SortOptions = new SortOptions("activityStartDatetime", "desc");
  activityTypeOptions = [];
  selectedActivity: any;
  private boundOnSearchIconClick: () => void;
  private searchIconEl: Element | null = null;

  @Output()
  private showDetails = new EventEmitter<string>();

  constructor(
    private utilService: UtilityService,
    private systemAdminService: SystemAdminService,
    public modalController: ModalController,
    private elementRef: ElementRef,
    private alertController: AlertController,
    private popoverCtrl: PopoverController,
    private resourceManagementService: ResourceManagementService
  ) {
    this.getActivitiesTypes();
  }

  ngAfterViewInit() {
    // attaching event on search icon explictly
    this.searchIconEl = this.elementRef.nativeElement.querySelector(
      ".searchbar-search-icon"
    );
    if (this.searchIconEl) {
      this.boundOnSearchIconClick = () => this.loadCompanyResource();
      this.searchIconEl.addEventListener("click", this.boundOnSearchIconClick);
    }
  }

  ngOnDestroy(): void {
    if (this.searchIconEl && this.boundOnSearchIconClick) {
      this.searchIconEl.removeEventListener(
        "click",
        this.boundOnSearchIconClick
      );
    }
  }

  ngOnInit(): void {
    this.selectedStartDate = this.activityLogValue.date;
    this.selectedEndDate = this.activityLogValue.date;

    const momentDate = moment(this.selectedStartDate);
    this.minDateValue = {
      year: momentDate.get("year"),
      month: momentDate.get("month") + 1,
      day: momentDate.date(),
    };

    this.loadCompanyResource();
  }

  @Input()
  set activityLogVal(activity: WorkforceActivity) {
    this.activityLogValue = activity;
  }

  get activityLogVal() {
    return this.activityLogValue;
  }

  @Input()
  set canAdd(hasAddAccess: boolean) {
    this.hasAddAccess = hasAddAccess;
  }

  get canAdd() {
    return this.hasAddAccess;
  }

  @Input()
  set canEdit(hasEditAccess: boolean) {
    this.hasEditAccess = hasEditAccess;
  }

  get canEdit() {
    return this.hasEditAccess;
  }

  @Input()
  set canDelete(hasDeleteAccess: boolean) {
    this.hasDeleteAccess = hasDeleteAccess;
  }

  get canDelete() {
    return this.hasDeleteAccess;
  }

  isHireToPresent() {
    this.hireToPresent = !this.hireToPresent;
    if (this.hireToPresent) {
      this.clearFilters();
    }
  }

  loadCompanyResource(selectedPage: number = 0): void {
    this.utilService.increaseSpinnerCount("Loading activity log");
    this.currentPage = typeof selectedPage === "number" ? selectedPage : 0;
    this.loadFilters();

    this.systemAdminService.getActivities(this.filterParameters).subscribe(
      (response) => {
        this.activityLog = [];
        if (response && response["hits"] && response["totalHits"] > 0) {
          this.activityLog = response["hits"] as WorkforceActivityDetails[];
          this.activityLog = this.activityLog.map((el) => {
            return {
              ...el,
              durationDisplay: moment
                .utc(el.duration * 60000)
                .format("H [hr] m [min]"),
              travelTimeDisplay: moment
                .utc(el.travelTime * 60000)
                .format("H [hr] m [min]"),
            };
          });
          this.totalPageCount = Math.ceil(
            response["totalHits"] / this.pageSize
          );

          this.currentPage =
            this.currentPage !== null &&
            this.currentPage !== undefined &&
            typeof selectedPage === "number"
              ? this.currentPage
              : 0;
        }
        this.utilService.decreaseSpinnerCount();
      },
      (err) => {
        this.utilService.decreaseSpinnerCount();
        let msg = "Error loading activities";
        console.error(err);
        this.utilService.presentErrorToast(err, msg);
      }
    );
  }

  onPageChange(selectedPage: number) {
    this.loadCompanyResource(selectedPage);
  }

  addActivityLog() {
    let data = {
      grid: this,
      page: this.currentPage,
      userDetails: this.activityLogValue,
    };
    let opts = { enableBackdropDismiss: false };

    this.showActivityModal(data, opts);
  }

  printActivityDetails() {
    print();
  }

  editActivityLog(a: WorkforceActivityDetails) {
    a.timeZone = this.activityLogValue.timeZone;
    let data = {
      grid: this,
      page: this.currentPage,
      activity: a,
    };
    let opts = { enableBackdropDismiss: false };

    this.showActivityModal(data, opts);
  }

  private showActivityModal(data: {}, opts: {}) {
    let modal = this.modalController.create(
      ActivityLogAddComponent,
      data,
      opts
    );
    modal
      .present()
      .then((res) => {})
      .catch((err) => {
        console.error(err);
      });
    modal.onDidDismiss(() => {
      setTimeout(() => {
        this.loadCompanyResource();
      }, 1500);
    });
  }

  onSearchCancel() {
    this.query = null;
    if (this.filterParameters && this.filterParameters.search) {
      this.filterParameters.search = null;
    }
    this.loadCompanyResource();
  }

  goBackActivityLog(action: string) {
    this.showDetails.emit(action);
  }

  loadFilters() {
    this.filterParameters.entityType = "EMPLOYEEACTIVITY";
    this.filterParameters.from = this.currentPage * this.pageSize;
    this.filterParameters.size = this.pageSize;
    this.filterParameters.mustFields = [
      { name: "userId", value: this.activityLogValue.userId },
    ];
    this.filterParameters.sortBy = {
      name: this.sortOptions.column,
      value: this.sortOptions.direction,
    };

    this.filterParameters.dateRanges = this.loadRangeDates();

    if (this.selectedActivityType) {
      let activityType = this.selectedActivityType;
      if (this.selectedActivityType === "In-Patient Visit")
        activityType = "In-Patient";
      else if (this.selectedActivityType === "Even More Care (EMC)")
        activityType = "EMC";
      this.filterParameters.mustFields.push({
        name: "activityType.keyword",
        value: activityType,
      });
    }

    if (this.query) {
      this.filterParameters.search = this.query;
    }
  }

  loadRangeDates() {
    if (!this.selectedStartDate && !this.selectedEndDate) {
      return null;
    } else {
      let dateRange: any = {};
      dateRange.name = "date";

      if (this.selectedStartDate) {
        dateRange.fromDate = moment(this.selectedStartDate).format(
          "YYYY-MM-DD"
        );
      }

      if (this.selectedEndDate) {
        dateRange.toDate = moment(this.selectedEndDate).format("YYYY-MM-DD");
      }

      return dateRange;
    }
  }

  validateDate(date, field) {
    const momentDate = moment(date);
    if (date) {
      if (field === "startDate") {
        this.minDateValue = {
          year: momentDate.get("year"),
          month: momentDate.get("month") + 1,
          day: momentDate.date(),
        };
      }

      if (
        moment(this.selectedStartDate).isAfter(moment(this.selectedEndDate))
      ) {
        this.selectedEndDate = this.selectedStartDate;
      }
    } else if (field === "startDate") {
      this.minDateValue = {
        year: momentDate.get("year") - 10,
        month: 1,
        day: 1,
      };
    }
  }

  clearFilters() {
    this.selectedStartDate = null;
    this.selectedEndDate = null;
    this.query = null;
    this.selectedActivityType = null;
    this.filterParameters = {};
    this.getMinDate();
    this.loadCompanyResource();
  }

  checkClearInput() {
    if (!this.query || this.query.trim().length === 0) {
      if (this.filterParameters && this.filterParameters.search) {
        this.filterParameters.search = null;
      }
      this.loadCompanyResource();
    }
  }

  getMinDate() {
    const momentDate = moment();
    this.minDateValue = {
      year: momentDate.get("year") - 10,
      month: momentDate.get("month") + 1,
      day: momentDate.date(),
    };
  }

  sortBy(selectedCol) {
    this.currentPage = 0;
    if (this.sortOptions.column === selectedCol) {
      this.sortOptions.direction =
        this.sortOptions.direction === "asc" ? "desc" : "asc";
    } else {
      this.sortOptions.column = selectedCol;
      this.sortOptions.direction = "asc";
    }

    this.loadCompanyResource();
  }

  showDeleteConfirmation(a: WorkforceActivityDetails) {
    let alert = this.alertController.create({
      title: "Delete Confirmation",
      message: "Are you sure you want to delete this activity?",
      buttons: [
        {
          text: "No",
          handler: () => {},
        },
        {
          text: "Yes",
          handler: () => {
            this.deleteActivity(a);
          },
        },
      ],
    });
    alert.present();
  }

  showActivityDetailsOptions($event, a: WorkforceActivityDetails) {
    a.timeZone = moment.tz.guess();
    let patientMoreOptions = this.getActivityMoreOptions();
    let popover = this.popoverCtrl.create(
      "ActivityLogOptionsPopover",
      {
        optionList: patientMoreOptions,
      },
      {
        cssClass: "calendar-visit-popover",
      }
    );

    popover.present({
      ev: $event,
    });

    popover.onDidDismiss((action) => {
      if (action && action.deleteEvent) {
        this.showDeleteConfirmation(a);
      }
      if (action && action.editEvent) {
        this.editActivityLog(a);
      }
    });
  }

  getActivityMoreOptions(): string[] {
    let activityMoreOptions: string[] = [];
    if (this.hasDeleteAccess) activityMoreOptions.push("Delete Event");
    if (this.hasEditAccess) activityMoreOptions.push("Edit Event");
    return activityMoreOptions;
  }

  deleteActivity(a: WorkforceActivityDetails) {
    a.isDeleted = true;
    let activities: WorkforceActivityDetails[] = [];
    activities.push(a);
    this.systemAdminService
      .deleteActivity(activities)
      .pipe()
      .take(1)
      .subscribe(
        (response) => {
          setTimeout(() => {
            this.loadCompanyResource();
          }, 1500);
        },
        (err) => {
          this.showAlert({ title: "Error deleting activity", message: err });
        },
        () => {}
      );
  }

  private getActivitiesTypes() {
    this.resourceManagementService
      .getLookUpValues()
      .pipe(take(1))
      .subscribe(
        (response) => {
          this.activityTypeOptions = response["ActivityType"].map((item) => {
            return {
              display: item.description,
              value: item.id,
            };
          });
        },
        (err) => {
          console.log(`Error loading lookups: ${err}`);
        }
      );
  }

  showAlert = ({ title, message }) => {
    let alert = this.alertController.create({
      title,
      message,
      buttons: [
        {
          text: "Ok",
          handler: () => {},
        },
      ],
    });
    alert.present();
  };

  checkActivitySourceAndPermissions(a: WorkforceActivityDetails) {
    return (
      a.source === "EMPLOYEEACTIVITYLOG" &&
      (this.hasEditAccess || this.hasDeleteAccess)
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/system-admin/activity-log-details/activity-log-details.ts